<template>
  <Transition name="slide-down">
    <div
      class="absolute top-2 left-1/2 -translate-x-1/2 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700 z-30"
    >
      <div class="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform rotate-45"></div>
      <div class="p-4 space-y-4 relative">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            POS Settings
          </h3>
          <button
            @click="$emit('close')"
            class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <SearchSelect
          v-model="settings.warehouse_id"
          label="Warehouse"
          end-point="system/company/warehouses"
          placeholder="Select Warehouse"
        />

        <SearchSelect
          v-model="settings.price_list_id"
          label="Price List"
          placeholder="Select price list"
          end-point="finance/master-data/price-lists"
        />

        <SearchSelect
          v-model="settings.sales_channel_id"
          label="Sales Channel"
          placeholder="Select sales channel"
          end-point="revenue/master-data/sales-channels"
        />

        <div class="flex items-center space-x-3 pt-2">
          <button
            @click="saveSettings"
            :disabled="saving"
            class="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ saving ? 'Saving...' : 'Save as Default' }}
          </button>
          <button
            @click="$emit('close')"
            class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import Toast from '../../../../../services/Toast'
import SearchSelect from '../../../../../components/ui/form/SearchSelect.vue'

interface POSSettings {
  warehouse_id: string
  price_list_id: string
  sales_channel_id: string
}

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'settings-loaded', settings: POSSettings): void
}>()

const saving = ref(false)
const settings = reactive<POSSettings>({
  warehouse_id: '',
  price_list_id: '',
  sales_channel_id: ''
})

const loadSettings = async () => {
  try {
    let warehouse_id = localStorage.getItem('fp_pos_warehouse_id') || ''
    let price_list_id = localStorage.getItem('fp_pos_price_list_id') || ''
    let sales_channel_id = localStorage.getItem('fp_pos_sales_channel_id') || ''

      settings.warehouse_id = warehouse_id || ''
      settings.price_list_id = price_list_id || ''
      settings.sales_channel_id = sales_channel_id || ''
  } catch (error) {
    console.error('Error loading settings:', error)
  }
}

const saveSettings = async () => {
  try {
    saving.value = true

    localStorage.setItem('fp_pos_warehouse_id', settings.warehouse_id)
    localStorage.setItem('fp_pos_price_list_id', settings.price_list_id)
    localStorage.setItem('fp_pos_sales_channel_id', settings.sales_channel_id)

    Toast.show('Settings saved as default', 'info')
    emit('close')
  } catch (error) {
    Toast.show('Failed to save settings', 'error')
  } finally {
    saving.value = false
  }
}


onMounted(() => {
  loadSettings()
})

</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out;
  transform-origin: top;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px) scaleY(0.95);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px) scaleY(0.95);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0) scaleY(1);
}
</style>
